import { makeStyles } from '@material-ui/styles';
import getAppData from '@connectors/newport/appData/getAppData';
import { useEffect, useState } from 'react';
import useUserStore from '@services/stores/UserStore';
import NavigationLayout from '@components/account/NavigationLayout';
import { useTranslation } from '@tools/i18n';
import useAccountStore from '@services/stores/AccountStore';
import { OrderHistoryItem, OrderItem } from '@services/types/MyNewportOrderTypes';
import If from '@components/misc/If';
import CallToActionButton from '@components/products/partials/CallToActionButton';
import theme from '@components/theme';
import { CircularProgress } from '@material-ui/core';
import { OrderHeader } from '@components/account/Order/OrderHeader';
import { GetServerSidePropsContext } from 'next';
import MyNewportImage from '@components/account/MyNewportImage';
import { AccountEmailVerified } from '@components/account/AccountEmailVerified';

// @ts-ignore
const useStyles = makeStyles({
    orderHeader: {
        display: 'grid',
        gap: 20,
        marginBottom: 20,
        gridTemplateColumns: '1fr 1fr',
        // @ts-ignore
        [theme.breakpoints?.up('sm')]: {
            gridTemplateColumns: '1fr 1fr 1fr',
        },
    },
    order: {
        paddingBottom: 20,
        marginBottom: 40,
        borderBottom: '1px solid' + theme.palette.primary.lightest,
    },
    orderActions: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    label: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    list: {
        display: 'grid',
        gap: 20,
        maxWidth: 1000,
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    items: {
        display: 'grid',
        gap: 10,
        gridTemplateColumns: '1fr 1fr 1fr',
        // @ts-ignore
        [theme.breakpoints?.up('md')]: {
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
        },
        maxWidth: 500,
        marginBottom: 20,
    },
}, { name: 'account' });


export default function AccountHistory() {
    const classes = useStyles();
    const user = useUserStore();
    const account = useAccountStore();
    const { t } = useTranslation(['account']);
    const [orders, setOrders] = useState<OrderHistoryItem[]>([]);
    const [loading, setLoading] = useState(false);

    // @ts-ignore
    useEffect(async () => {
        setLoading(true);
        const orders = await account.methods.getOrders();
        if (Array.isArray(orders)) {
            setOrders(orders || []);
        }
        setLoading(false);
    }, [user.isLoggedIn]);

    function renderItems(items: OrderItem[]) {
        if (!Array.isArray(items)) {
            return [];
        }

        return items.map((item) => {
            return <MyNewportImage productUuid={item.product.uuid} alt={item.product.name}></MyNewportImage>;
        }).slice(0, 6);
    }

    function renderList() {
        if (!user.isLoggedIn) {
            return null;
        }

        return orders?.map((order) => {
            if (order.order_type === 'physical') {
                return <article className={classes.order}>
                    <h3>{order.store_name}</h3>
                    <OrderHeader
                        type={order.order_type}
                        reference={order.reference}
                        status={order.status}
                        total={order.totals.total}
                        currency={order.currency}
                        store={order.store_name}
                        date={order.created_at}
                    />
                    <section className={classes.items}>
                        {renderItems(order.items)}
                    </section>
                </article>
            }

            return <article className={classes.order}>
                <h3>#{order.id}</h3>
                <OrderHeader
                    type={order.order_type}
                    reference={order.id}
                    status={order.status}
                    total={order.totals.total}
                    currency={order.currency}
                    store={order?.model?.store?.domain || order?.store_name || ('not set)')}
                    date={order.created_at}
                />
                <section className={classes.items}>
                    {renderItems(order.items)}
                </section>
                <div className={classes.orderActions}>
                    <If condition={order.model.can_be_returned}>
                        <CallToActionButton
                            href={user.getService().createLink('/returns')}
                            colorScheme={'bg-light'}
                            size={'small'}>
                            {t('account:button-go-to-returns')}
                        </CallToActionButton>
                    </If>
                    <CallToActionButton href={`/account/order/details/${order.id}`} size={'small'}>
                        {t('account:go-to-order')}
                    </CallToActionButton>
                </div>
            </article>;
        }) || null;
    }

    function renderSections() {
        if (loading) {
            return <div className={classes.loading}>
                <CircularProgress size={48}></CircularProgress>
            </div>;
        }

        if (orders?.length) {
            return renderList();
        }

        return <>
            {t('account:order-history-empty')}
        </>
    }

    return <NavigationLayout
        title={t('account:order-history')}
        loading={loading}
    >
        <section className={classes.list}>
            <AccountEmailVerified />
            {renderSections()}
        </section>
    </NavigationLayout>;
}

export const getServerSideProps = async ({ req }: GetServerSidePropsContext) => {
    const appData = await getAppData(req);
    return {
        props: {
            appData,
        },
    };
};
